import type { MapRef } from 'react-map-gl/mapbox';

const CAPTURE_TIMEOUT_MS = 10_000;

interface EncodeOptions {
  quality?: number;
  maxSizeBytes?: number;
  minQuality?: number;
}

async function encodeWithinSizeBudget(
  canvas: OffscreenCanvas,
  mimeType: string,
  encodeOptions?: EncodeOptions,
): Promise<Blob> {
  let quality = encodeOptions?.quality;
  let blob = await canvas.convertToBlob(quality === undefined ? { type: mimeType } : { type: mimeType, quality });

  const maxSizeBytes = encodeOptions?.maxSizeBytes;
  const minQuality = encodeOptions?.minQuality ?? 0.5;
  while (maxSizeBytes !== undefined && blob.size > maxSizeBytes && quality !== undefined && quality > minQuality) {
    quality = Math.max(minQuality, Math.round((quality - 0.1) * 10) / 10);
    blob = await canvas.convertToBlob({ type: mimeType, quality });
  }

  return blob;
}

// Captures the screenshot of the map canvas and return a base64 data URL.
export function captureMapCanvas(
  map: MapRef,
  canvas: OffscreenCanvas,
  draw: (mapCanvas: HTMLCanvasElement, ctx: OffscreenCanvasRenderingContext2D) => void,
  mimeType: string,
  encodeOptions?: EncodeOptions,
): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;

    const settleResolve = (value: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      resolve(value);
    };

    const settleReject = (error: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      reject(error);
    };

    const handleRender = async () => {
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          settleReject(new Error('Failed to get 2d context from OffscreenCanvas'));
          return;
        }

        draw(map.getCanvas(), ctx);

        const blob = await encodeWithinSizeBudget(canvas, mimeType, encodeOptions);
        const fileReader = new FileReader();
        fileReader.addEventListener('load', (e) => settleResolve(e.target?.result as string));
        fileReader.addEventListener('error', () =>
          settleReject(fileReader.error ?? new Error('Failed to read map canvas blob as data URL')),
        );
        fileReader.readAsDataURL(blob);
      } catch (error) {
        settleReject(error instanceof Error ? error : new Error(String(error)));
      }
    };

    const timeoutId = setTimeout(() => {
      map.off('render', handleRender);
      settleReject(new Error('Timed out waiting for map render to capture canvas'));
    }, CAPTURE_TIMEOUT_MS);

    map.once('render', handleRender);
    map.setBearing(map.getBearing());
  });
}
